import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Pause, Play } from "lucide-react";
import { TOK, SERIF, SANS } from "../soulumoCreate/shared";
import { TalkingPortrait } from "../coach/TalkingPortrait";
import { ADELAIDA_IMG, Bubble, GlassPlay, NextBtn, StepTitle } from "./DemoBits";

const ADELAIDA = { name: "Adelaida", image: ADELAIDA_IMG };

export const WelcomeStep = ({ line, voice, onNext }) => {
  const [started, setStarted] = useState(false);
  return (
    <div className="text-center" data-testid="demo-step-welcome">
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} data-testid="demo-adelaida-portrait">
        <TalkingPortrait who={ADELAIDA} audioEl={voice.el} env={line?.env} speaking={voice.playing} status="idle" />
      </motion.div>
      <h1 className="mt-6 text-4xl font-light" style={{ fontFamily: SERIF, color: TOK.cream }}>Meet Adelaida</h1>
      <p className="mt-4 text-lg leading-relaxed" style={{ fontFamily: SERIF, color: TOK.creamDim }}>{line?.text}</p>
      {!started
        ? <NextBtn testId="demo-start-btn" onClick={() => { setStarted(true); voice.play(line?.audio_url); }}>Start the demo</NextBtn>
        : <NextBtn testId="demo-welcome-next" onClick={onNext}>Show me</NextBtn>}
      <p className="mt-3 text-xs" style={{ color: TOK.creamDim, fontFamily: SANS }}>A two-minute guided demo. You'll follow Andrew as he makes his installation.</p>
    </div>
  );
};

export const ChatStep = ({ demo, voice, onNext }) => {
  const [n, setN] = useState(1);
  useEffect(() => { if (n === 3) voice.play(demo.lines.words.audio_url); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="demo-step-chat">
      <StepTitle kicker="Step 1 · Tell Adelaida" title="Andrew says what he'd like to change" />
      <div className="space-y-4">
        <Bubble from="adelaida" testId="demo-chat-q">What would you like your installation to help you with?</Bubble>
        {n >= 2 && <Bubble from="michael" testId="demo-chat-michael">{demo.situation}</Bubble>}
        {n >= 3 && <Bubble from="adelaida" testId="demo-chat-reply" delay={0.4}>{demo.lines.words.text} Here's what I've written for you, Andrew.</Bubble>}
      </div>
      {n < 3 ? <NextBtn testId="demo-chat-next" onClick={() => setN(n + 1)}>{n === 1 ? "See Andrew's answer" : "See Adelaida's reply"}</NextBtn>
        : <NextBtn testId="demo-chat-done" onClick={onNext}>See his affirmations</NextBtn>}
    </div>
  );
};

export const WordsStep = ({ demo, onNext }) => (
  <div data-testid="demo-step-words">
    <StepTitle kicker="Step 2 · His words" title="Affirmations written around Andrew" />
    <div className="space-y-2.5">
      {demo.affirmations.map((a, i) => (
        <motion.p key={a} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.12 * i }}
          className="rounded-2xl px-4 py-3 text-lg" style={{ background: "rgba(245,236,217,0.05)", border: "1px solid rgba(245,236,217,0.1)", fontFamily: SERIF, color: TOK.cream }}
          data-testid={`demo-affirmation-${i}`}>{a}</motion.p>
      ))}
    </div>
    <p className="mt-4 text-sm leading-relaxed" style={{ color: TOK.creamDim, fontFamily: SANS }} data-testid="demo-words-sample-note">
      These are just 5 of Andrew's affirmations. A full installation carries around 24, and takes them on a journey: heard clearly first,
      then settling as the mind slows, then sinking beneath conscious thought, until they feel like his own. Members can keep every line just right, or adjust any of them.
    </p>
    <NextBtn testId="demo-words-next" onClick={onNext}>Just right</NextBtn>
  </div>
);

export const VoiceStep = ({ demo, voice, onNext }) => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    voice.play(demo.lines.voice.audio_url);
    const t = setInterval(() => setPct((p) => Math.min(100, p + 4)), 160);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="demo-step-voice">
      <StepTitle kicker="Step 3 · His voice" title="Andrew records a short sample" />
      <div className="rounded-3xl p-6 text-center" style={{ background: "rgba(245,236,217,0.05)", border: "1px solid rgba(245,236,217,0.1)" }}>
        <motion.div animate={{ scale: pct < 100 ? [1, 1.08, 1] : 1 }} transition={{ repeat: pct < 100 ? Infinity : 0, duration: 1.4 }}
          className="mx-auto w-20 h-20 rounded-full grid place-items-center" style={{ background: "rgba(215,187,122,0.18)" }}>
          <Mic size={30} style={{ color: TOK.gold }} />
        </motion.div>
        <div className="mt-5 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(245,236,217,0.12)" }}>
          <div className="h-full transition-[width] duration-150" style={{ width: `${pct}%`, background: TOK.gold }} />
        </div>
        <p className="mt-3 text-sm" style={{ color: TOK.creamDim, fontFamily: SANS }} data-testid="demo-voice-status">
          {pct < 100 ? "Andrew reads a short passage for two minutes…" : "Done. Soulumo has learned Andrew's voice."}
        </p>
      </div>
      <p className="mt-5 text-lg italic" style={{ fontFamily: SERIF, color: TOK.creamDim }}>{demo.lines.voice.text}</p>
      <NextBtn testId="demo-voice-next" onClick={onNext} disabled={pct < 100}>Next</NextBtn>
    </div>
  );
};

export const MusicStep = ({ demo, voice, onNext }) => {
  const [pick, setPick] = useState(null);
  useEffect(() => { voice.play(demo.lines.music.audio_url); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="demo-step-music">
      <StepTitle kicker="Step 4 · Mood & music" title="Pick the feel of Andrew's installation" />
      <div className="flex flex-wrap gap-2.5">
        {demo.moods.map((m, i) => (
          <button key={m} type="button" onClick={() => setPick(m)} data-testid={`demo-mood-${i}`}
            className="rounded-full px-5 py-3 text-sm transition-[background-color,border-color] duration-200"
            style={{ fontFamily: SANS, color: TOK.cream, border: `1px solid ${pick === m ? TOK.gold : "rgba(245,236,217,0.2)"}`, background: pick === m ? "rgba(215,187,122,0.16)" : "transparent" }}>{m}</button>
        ))}
      </div>
      <p className="mt-5 text-lg italic" style={{ fontFamily: SERIF, color: TOK.creamDim }}>{demo.lines.music.text}</p>
      <NextBtn testId="demo-music-next" onClick={onNext} disabled={!pick}>Create Andrew's installation</NextBtn>
    </div>
  );
};

export const BuildStep = ({ onNext }) => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPct((p) => { if (p >= 100) { clearInterval(t); setTimeout(onNext, 400); return 100; } return p + 5; }), 140);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const label = pct < 35 ? "Weaving his affirmations…" : pct < 70 ? "Speaking them in Andrew's voice…" : "Layering the music…";
  return (
    <div className="text-center pt-10" data-testid="demo-step-build">
      <StepTitle kicker="Step 5" title="Creating Andrew's installation" />
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(245,236,217,0.12)" }}>
        <div className="h-full transition-[width] duration-150" style={{ width: `${pct}%`, background: TOK.gold }} />
      </div>
      <p className="mt-4 text-sm" style={{ color: TOK.creamDim, fontFamily: SANS }}>{label}</p>
    </div>
  );
};

const LAYERS = [
  { at: 0, title: "Heard clearly", line: "Andrew's own voice, every word distinct." },
  { at: 9, title: "Settling in", line: "The voice softens as a theta tone slows the mind." },
  { at: 18, title: "Sinking beneath thought", line: "Whispered under the music, where the subconscious takes it in." },
];

export const ListenStep = ({ demo, voice, onNext }) => {
  const [stage, setStage] = useState("ready");
  const listen = () => { setStage("clip"); voice.play(demo.clip_url, () => { setStage("bridge"); voice.play(demo.lines.bridge.audio_url, () => setStage("done")); }); };
  const t = stage === "clip" ? voice.time : stage === "ready" ? -1 : 99;
  return (
    <div data-testid="demo-step-listen">
      <StepTitle kicker="Step 6 · Hear it" title="Andrew's installation, in his own voice" />
      <GlassPlay playing={stage === "clip"} onClick={stage === "clip" ? voice.stop : listen} testId="demo-listen-btn" Icon={stage === "clip" ? Pause : Play} />
      <p className="mt-4 text-center text-sm" style={{ color: TOK.creamDim, fontFamily: SANS }}>30 seconds · headphones on if you can</p>
      <div className="mt-6 space-y-2" data-testid="demo-layers">
        {LAYERS.map((l, i) => {
          const on = t >= l.at && (i === LAYERS.length - 1 || t < LAYERS[i + 1].at);
          const past = t >= l.at;
          return (
            <div key={l.title} className="flex gap-3 rounded-2xl px-4 py-3 transition-[background-color,opacity] duration-500"
              style={{ background: on ? "rgba(215,187,122,0.12)" : "transparent", opacity: past || t < 0 ? 1 : 0.45 }} data-testid={`demo-layer-${i}`}>
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: past ? TOK.gold : "rgba(245,236,217,0.25)" }} />
              <div>
                <p className="text-base" style={{ fontFamily: SERIF, color: TOK.cream }}>{l.title}</p>
                <p className="text-xs" style={{ fontFamily: SANS, color: TOK.creamDim }}>{l.line}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-sm italic" style={{ fontFamily: SERIF, color: TOK.creamDim }}>Not a list of affirmations: a full installation moves through these layers many times, with music and brainwave tones designed around you.</p>
      {(stage === "bridge" || stage === "done") && <div className="mt-6"><Bubble from="adelaida" testId="demo-bridge">{demo.lines.bridge.text}</Bubble></div>}
      <NextBtn testId="demo-make-mine-btn" onClick={onNext}>{stage === "done" || stage === "bridge" ? "Make it mine" : "Skip to make it mine"}</NextBtn>
    </div>
  );
};
